import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { UploadManagerProvider } from "@/context/UploadManagerContext";
import { UploadManagerWidget } from "@/components/UploadManagerWidget";
import Landing from "@/pages/Landing";
import AuthedHome from "@/pages/AuthedHome";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import EventEditor from "@/pages/EventEditor";
import Vault from "@/pages/Vault";
import FAQ from "@/pages/FAQ";
import { PrivacyPolicy, TermsOfService, CopyrightPolicy } from "@/pages/Legal";
import Payouts from "@/pages/Payouts";
import AuthCallback from "@/pages/AuthCallback";
import PaymentSuccess from "@/pages/PaymentSuccess";
import { Loader2 } from "lucide-react";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen grid place-items-center bg-[#050505]">
        <Loader2 className="h-8 w-8 animate-spin text-[#00F0FF]" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function HomeRoute() {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen grid place-items-center bg-[#050505]">
        <Loader2 className="h-8 w-8 animate-spin text-[#00F0FF]" />
      </div>
    );
  return user ? <AuthedHome /> : <Landing />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<HomeRoute />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/payouts"
        element={
          <ProtectedRoute>
            <Payouts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/events/:eventId"
        element={
          <ProtectedRoute>
            <EventEditor />
          </ProtectedRoute>
        }
      />
      <Route path="/vault/:slug" element={<Vault />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/copyright" element={<CopyrightPolicy />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <UploadManagerProvider>
            <Toaster theme="dark" position="top-center" richColors />
            <AppRouter />
            <UploadManagerWidget />
          </UploadManagerProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
